import { theme } from '@prophecy/theme';
import { useState } from 'react';
import Truncate, { TruncateProps } from 'react-truncate';
import styled from 'styled-components';

import { Button } from '../Button';
import { Stack } from '../Layout';

const StyledButton = styled(Button)`
  padding: 0;
  height: auto;
  word-break: normal;
  &&&:focus,
  &&&:active,
  &&&[data-active='true'],
  &&&:focus-visible {
    border: 0;
  }
`;

type MultilineEllipsisProps = TruncateProps & {
  expandable?: boolean;
  collapsible?: boolean;
  symbol?: React.ReactNode;
  collapseSymbol?: React.ReactNode;
  lines?: number;
};

export function MultilineEllipsis({
  children,
  expandable: _expandable,
  collapsible,
  symbol = '...',
  collapseSymbol = 'Less',
  lines,
  ...rest
}: MultilineEllipsisProps) {
  const [expanded, setExpanded] = useState(false);

  const stopPropagationAndDefault = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    e.nativeEvent.stopImmediatePropagation();
  };

  let ellipsis = symbol;
  if (collapsible) {
    ellipsis = (
      <StyledButton
        variant='link'
        onPointerUp={(e: React.MouseEvent) => {
          stopPropagationAndDefault(e);
          setExpanded(true);
        }}
        data-test-id='multiline-ellipsis-expand-button'>
        {symbol}
      </StyledButton>
    );
  }

  return (
    <Stack gap={theme.spaces.x8} direction='horizontal' alignY='end'>
      <Truncate {...rest} lines={expanded ? false : lines} ellipsis={ellipsis}>
        {children}
      </Truncate>
      {collapsible && expanded && (
        <StyledButton
          variant='link'
          onPointerUp={(e: React.MouseEvent) => {
            stopPropagationAndDefault(e);
            setExpanded(false);
          }}
          data-test-id='multiline-ellipsis-collapse-button'>
          {collapseSymbol}
        </StyledButton>
      )}
    </Stack>
  );
}
